export function normalizeApiOrigin(raw) {
  let s = String(raw || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) {
    try { return new URL(s).origin; } catch { return s.replace(/\/+$/, ''); }
  }
  const hostish = s.replace(/^\/+/, '').replace(/\/+$/, '');
  return hostish ? `http://${hostish.split('/')[0]}` : '';
}

function isLocalDevHost(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function isDeployedHost(hostname) {
  return typeof hostname === 'string' && hostname.length > 0 && !isLocalDevHost(hostname);
}

/** Baked API hosts that must not be used on the deployed SPA. */
function isStaleBakedApiHost(apiHost, pageHost) {
  if (!apiHost || !pageHost || !isDeployedHost(pageHost)) return false;
  if (apiHost !== pageHost) return true;
  return apiHost.endsWith('.bestlooks.dev') || apiHost.endsWith('.bestlooks.net');
}

/** Drop stale baked API hosts that do not match the page origin. */
export function sanitizeApiBaseUrl(baseUrl) {
  if (typeof window === 'undefined' || !window.location?.hostname) return baseUrl;

  const pageHost = window.location.hostname;
  if (isLocalDevHost(pageHost)) return baseUrl;

  const normalized = normalizeApiOrigin(baseUrl);
  if (!normalized) return '';

  try {
    const apiHost = new URL(normalized).hostname;
    if (isStaleBakedApiHost(apiHost, pageHost)) return '';
  } catch {
    return '';
  }

  return normalized;
}

/** Rewrite absolute API URLs that target a stale host to same-origin paths. */
export function rewriteStaleApiRequest(config) {
  if (typeof window === 'undefined' || !config?.url) return config;

  const pageHost = window.location.hostname;
  if (isLocalDevHost(pageHost)) return config;

  const base = config.baseURL || '';
  let resolved;
  try {
    resolved = new URL(config.url, base || window.location.origin);
  } catch {
    return config;
  }

  if (!isStaleBakedApiHost(resolved.hostname, pageHost) && resolved.hostname === pageHost) {
    return config;
  }

  if (isStaleBakedApiHost(resolved.hostname, pageHost) || resolved.hostname !== pageHost) {
    config.baseURL = '';
    config.url = `${resolved.pathname}${resolved.search}`;
  }

  return config;
}

export function resolveApiBaseUrl() {
  const flag = String(process.env.VUE_APP_DEV_USE_PROXY || '').trim().toLowerCase();
  const useProxy = flag === '1' || flag === 'true';
  if (useProxy && typeof window !== 'undefined' && window.location?.port === '8080') return '';

  // Deployed SPA: always same-origin /v1 — never a baked absolute API host.
  if (typeof window !== 'undefined' && window.location?.hostname) {
    if (!isLocalDevHost(window.location.hostname)) return '';
  }

  // Local dev only: optional direct backend URL from env.
  if (typeof window !== 'undefined' && isLocalDevHost(window.location.hostname)) {
    return sanitizeApiBaseUrl(normalizeApiOrigin(process.env.VUE_APP_API_URL));
  }

  return '';
}

/** Relative API path on deployed hosts; absolute only for local dev with env base URL. */
export function buildApiUrl(path) {
  const relativePath = path.startsWith('/') ? path : `/${path}`;
  const base = resolveApiBaseUrl();
  if (!base) return relativePath;
  return `${base.replace(/\/$/, '')}${relativePath}`;
}
